'use client';

import React from 'react';
import { getAvailability, type Product } from '@/lib/products';
import { useCart } from '@/context/CartContext';
import { useI18n } from '@/i18n/LocaleProvider';

interface AddToCartButtonProps {
  product: Product;
  quantity?: number;
  size?: 'sm' | 'lg';
  className?: string;
}

/** Add-to-cart / pre-order button, or a disabled "Sold Out" button when the product can't be ordered. */
export default function AddToCartButton({ product, quantity = 1, size = 'sm', className = '' }: AddToCartButtonProps) {
  const { addItem } = useCart();
  const { t } = useI18n();
  const availability = getAvailability(product);
  const sizeClasses = size === 'lg' ? 'py-4 px-8 text-sm' : 'text-xs px-3 py-1.5';

  if (availability === 'sold_out') {
    return (
      <button
        disabled
        className={`rounded-full font-bold uppercase tracking-wider bg-red-500/10 text-red-500 border-2 border-red-500 cursor-not-allowed ${sizeClasses} ${className}`}
      >
        {t.common.soldOut}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        // Cards wrap this button in a link to the product page.
        e.preventDefault();
        e.stopPropagation();
        addItem(product.id, quantity);
      }}
      className={`rounded-full font-bold uppercase tracking-wider bg-primary text-primary-foreground hover:bg-primary/90 transition-colors ${sizeClasses} ${className}`}
    >
      {availability === 'preorder' ? t.shop.preorder : t.shop.addToCart}
    </button>
  );
}
